import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Search } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@roger/ui-components';
import {
  getGoalOptionsForEpisode,
  getGoalTemplatesForEpisode,
} from '../../services/api';
import type {
  GoalTemplateDetail,
  GoalTemplateOptionWithDisciplines,
} from '../../services/api/types.gen';
import { useToast } from '../../hooks/useToastContext';
import CreateGoalFromTemplateCard from './CreateGoalFromTemplateCard';
import type { DisciplineType } from '../../services/api/models/DisciplineType';
import GoalOptionCard from './GoalOptionCard';

interface AddExternalEmrGoalsDialogProps {
  open: boolean;
  onClose: () => void;
  episodeId: string;
  episodeStartDate: string;
  episodeEndDate: string;
  /** Called after each goal saves, so the parent can refresh its goal lists. */
  onGoalAdded?: () => void;
}

const ALL_DISCIPLINES = 'all';

/**
 * External-EMR migration flow (episode-scoped goals): an episode migrated from
 * another EMR may have no visit that can open goals, so this dialog saves
 * goals straight onto the episode. Because there is no visit to scope the
 * template options to one discipline, the picker spans all disciplines and
 * lets the user filter by them — that discipline UI exists only on this
 * route, not in the visit-scoped AddGoalsBox. Only rendered for
 * Kinnser-enabled agencies; the backend enforces the same gate.
 */
const AddExternalEmrGoalsDialog: React.FC<AddExternalEmrGoalsDialogProps> = ({
  open,
  onClose,
  episodeId,
  episodeStartDate,
  episodeEndDate,
  onGoalAdded,
}) => {
  // 'select' shows the template picker; 'create' shows the goal cards being
  // filled in (template-based and free-text).
  const [view, setView] = useState<'select' | 'create'>('select');
  const [goalOptions, setGoalOptions] = useState<
    GoalTemplateOptionWithDisciplines[]
  >([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [disciplineFilter, setDisciplineFilter] = useState(ALL_DISCIPLINES);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Each pending template card carries the discipline it is added under:
  // The active filter when it was picked, else the option's only
  // discipline. An option under several disciplines picked with no filter
  // leaves it unset and the card asks.
  const [templatesFromCreation, setTemplatesFromCreation] = useState<
    Array<{
      template: GoalTemplateDetail;
      discipline?: DisciplineType;
      servedDisciplines: DisciplineType[];
    }>
  >([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [freeTextKeys, setFreeTextKeys] = useState<string[]>([]);
  const [hasSavedGoal, setHasSavedGoal] = useState(false);
  const { error: showError } = useToast();

  // Resets all component state when the dialog is closed. IMPORTANT: When
  // adding new state variables, add them to this function so the component
  // starts fresh each time it's opened.
  const resetDialogState = () => {
    setView('select');
    setGoalOptions([]);
    setIsLoadingOptions(false);
    setHasError(false);
    setSearchQuery('');
    setDisciplineFilter(ALL_DISCIPLINES);
    setSelectedIds(new Set());
    setTemplatesFromCreation([]);
    setIsLoadingTemplates(false);
    setFreeTextKeys([]);
    setHasSavedGoal(false);
  };

  useEffect(() => {
    if (!open) {
      resetDialogState();
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setIsLoadingOptions(true);
    setHasError(false);
    getGoalOptionsForEpisode({
      path: { episode_id: episodeId },
      throwOnError: true,
    })
      .then(response => {
        setGoalOptions(response.data ?? []);
      })
      .catch(() => {
        setHasError(true);
        showError(
          'Failed to load goal options',
          'Please close this window and try again'
        );
      })
      .finally(() => {
        setIsLoadingOptions(false);
      });
  }, [open, episodeId]);

  const availableDisciplines = useMemo(
    () =>
      Array.from(
        new Set(goalOptions.flatMap(option => option.disciplines))
      ).sort(),
    [goalOptions]
  );

  const filteredGoalOptions = goalOptions.filter(
    option =>
      option.name.toLowerCase().includes(searchQuery.trim().toLowerCase()) &&
      (disciplineFilter === ALL_DISCIPLINES ||
        option.disciplines.includes(disciplineFilter))
  );

  const toggleSelection = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleUseFreeText = () => {
    setFreeTextKeys(prev => [...prev, `external-emr-goal-${Date.now()}`]);
    setView('create');
  };

  // Stays on the picker until the templates load: switching views before the
  // request settles would show an empty creation view, and a failure would
  // drop the user's selection.
  const handleContinue = () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setIsLoadingTemplates(true);
    getGoalTemplatesForEpisode({
      path: { episode_id: episodeId },
      body: ids,
      throwOnError: true,
    })
      .then(response => {
        const newTemplates = response.data ?? [];
        const disciplinesById = new Map(
          goalOptions.map(option => [option.id, option.disciplines])
        );
        const pickedDisciplineFor = (
          templateId: string
        ): DisciplineType | undefined => {
          const served = disciplinesById.get(templateId) ?? [];
          if (
            disciplineFilter !== ALL_DISCIPLINES &&
            served.includes(disciplineFilter)
          ) {
            return disciplineFilter as DisciplineType;
          }
          return served.length === 1
            ? (served[0] as DisciplineType)
            : undefined;
        };
        setTemplatesFromCreation(prev => {
          const existingIds = new Set(prev.map(t => t.template.id));
          return [
            ...prev,
            ...newTemplates
              .filter(t => !existingIds.has(t.id))
              .map(template => ({
                template,
                discipline: pickedDisciplineFor(template.id),
                servedDisciplines: (disciplinesById.get(template.id) ??
                  []) as DisciplineType[],
              })),
          ];
        });
        setSelectedIds(new Set());
        setView('create');
      })
      .catch(() => {
        showError(
          'Error selecting templates',
          'There was an error selecting those templates.'
        );
      })
      .finally(() => {
        setIsLoadingTemplates(false);
      });
  };

  const handleRemoveTemplateCard = (templateId: string) => {
    setTemplatesFromCreation(prev =>
      prev.filter(t => t.template.id !== templateId)
    );
  };

  const handleTemplateGoalSaved = (templateId: string) => {
    handleRemoveTemplateCard(templateId);
    setHasSavedGoal(true);
    onGoalAdded?.();
  };

  const handleRemoveFreeTextCard = (key: string) => {
    setFreeTextKeys(prev => prev.filter(k => k !== key));
  };

  const handleFreeTextGoalSaved = (key: string) => {
    handleRemoveFreeTextCard(key);
    setHasSavedGoal(true);
    onGoalAdded?.();
  };

  const selectView = (
    <>
      {!isLoadingOptions && !hasError && goalOptions.length > 0 && (
        <div className='flex-shrink-0 space-y-2'>
          <div className='relative'>
            <Search className='pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground' />
            <Input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder='Search goals...'
              inputSize='sm'
              className='pl-8'
            />
          </div>
          {availableDisciplines.length > 0 && (
            <div className='flex flex-wrap items-center gap-1.5'>
              {[ALL_DISCIPLINES, ...availableDisciplines].map(discipline => (
                <button
                  key={discipline}
                  type='button'
                  onClick={() => setDisciplineFilter(discipline)}
                  className={`rounded-full border px-2.5 py-0.5 text-xs whitespace-nowrap transition-colors ${
                    disciplineFilter === discipline
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-background text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {discipline === ALL_DISCIPLINES ? 'All' : discipline}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <div className='flex-1 min-h-0 overflow-y-auto pr-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'>
        {isLoadingOptions ? (
          <div className='flex h-full items-center justify-center'>
            <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
          </div>
        ) : hasError ? (
          <p className='py-8 text-center text-sm text-muted-foreground'>
            Please try again. If the issue persists, contact your administrator
          </p>
        ) : goalOptions.length === 0 ? (
          <p className='py-8 text-center text-sm text-muted-foreground'>
            There are no goals available. You can still add a free text goal.
          </p>
        ) : filteredGoalOptions.length === 0 ? (
          <p className='py-8 text-center text-sm text-muted-foreground'>
            No matching goals
          </p>
        ) : (
          <div className='space-y-1.5 pr-1'>
            {filteredGoalOptions.map(option => (
              <GoalOptionCard
                key={option.id}
                goalOption={option}
                selected={selectedIds.has(option.id)}
                onClick={() => toggleSelection(option.id)}
                disciplines={option.disciplines}
              />
            ))}
          </div>
        )}
      </div>
      <DialogFooter className='flex-shrink-0 w-full grid grid-cols-[1fr_auto] items-center'>
        <Button
          variant='outline'
          size='sm'
          className='justify-self-start'
          onClick={handleUseFreeText}
        >
          Use Free Text
        </Button>
        <div className='flex gap-2'>
          {templatesFromCreation.length > 0 || freeTextKeys.length > 0 ? (
            <Button
              variant='outline'
              size='sm'
              onClick={() => setView('create')}
            >
              Back
            </Button>
          ) : (
            <Button variant='outline' size='sm' onClick={onClose}>
              Cancel
            </Button>
          )}
          <Button
            size='sm'
            onClick={handleContinue}
            disabled={selectedIds.size === 0 || isLoadingTemplates}
          >
            {isLoadingTemplates && (
              <Loader2 className='mr-1 h-3.5 w-3.5 animate-spin' />
            )}
            Continue ({selectedIds.size})
          </Button>
        </div>
      </DialogFooter>
    </>
  );

  const createView = (
    <>
      <div className='flex-1 min-h-0 overflow-y-auto pr-1 space-y-2'>
        {templatesFromCreation.map(entry => (
          <CreateGoalFromTemplateCard
            key={entry.template.id}
            template={entry.template}
            episodeId={episodeId}
            episodeStartDate={episodeStartDate}
            episodeEndDate={episodeEndDate}
            discipline={entry.discipline}
            servedDisciplines={entry.servedDisciplines}
            onSave={() => handleTemplateGoalSaved(entry.template.id)}
            onCancel={() => handleRemoveTemplateCard(entry.template.id)}
          />
        ))}
        {freeTextKeys.map(key => (
          <CreateGoalFromTemplateCard
            key={key}
            template={null}
            episodeId={episodeId}
            episodeStartDate={episodeStartDate}
            episodeEndDate={episodeEndDate}
            onSave={() => handleFreeTextGoalSaved(key)}
            onCancel={() => handleRemoveFreeTextCard(key)}
          />
        ))}
        {templatesFromCreation.length === 0 && freeTextKeys.length === 0 && (
          <p className='py-4 text-center text-sm text-muted-foreground'>
            {hasSavedGoal
              ? 'Goal saved. You can add another goal or close this window.'
              : 'No goals selected. Add more goals or close this window.'}
          </p>
        )}
      </div>
      <div className='flex justify-start pt-1 flex-shrink-0'>
        <Button
          size='sm'
          variant='outline'
          onClick={() => setView('select')}
          className='flex items-center gap-1'
        >
          <Plus className='h-4 w-4' />
          Add More Goals
        </Button>
      </div>
    </>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className='w-[calc(100vw-2rem)] sm:max-w-lg h-[75vh] flex flex-col overflow-hidden'>
        <DialogHeader className='flex-shrink-0'>
          <DialogTitle>Add Goals from External EMR</DialogTitle>
          <DialogDescription>
            Use this for goals that came from another EMR. These goals save to
            the episode, not to a visit.
          </DialogDescription>
        </DialogHeader>
        {view === 'select' ? selectView : createView}
      </DialogContent>
    </Dialog>
  );
};

export default AddExternalEmrGoalsDialog;
